import type { PricesTimeGranularity } from '@/types/api/prices'

import {
  addDaysToDate,
  DateFormat,
  dateToISOString,
  formatDate,
} from '@/utils/dates/dateFormatting'

import { DisplayMode, TimeRange } from './types'

interface TimeRangeParams {
  start: string
  end: string
  granularity: PricesTimeGranularity
}

/**
 * Generates time range parameters based on the selected range.
 * Returns start date, end date, and data granularity appropriate for the range.
 *
 * @param range - Time range selection (e.g., '1D', '1W', '1M', etc.)
 * @returns TimeRangeParams object
 *
 * @example
 * // For '1M' (One Month):
 * // - start: 30 days ago (e.g., "2024-01-21")
 * // - end: tomorrow's date (e.g., "2024-02-21")
 * // - granularity: "1day"
 * getTimeRangeParams('1M')
 */
export function getTimeRangeParams(range: string): TimeRangeParams {
  const now = new Date()
  // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
  const end = dateToISOString(addDaysToDate(now, 1)).split('T')[0]!

  switch (range) {
    case TimeRange.ONE_DAY:
      return {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        start: dateToISOString(now).split('T')[0]!,
        end,
        granularity: '1min',
      }
    case TimeRange.ONE_WEEK:
      return {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        start: dateToISOString(addDaysToDate(now, -7)).split('T')[0]!,
        end,
        granularity: '15min',
      }
    case TimeRange.ONE_MONTH:
      return {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        start: dateToISOString(addDaysToDate(now, -30)).split('T')[0]!,
        end,
        granularity: '1day',
      }
    case TimeRange.THREE_MONTHS:
      return {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        start: dateToISOString(addDaysToDate(now, -90)).split('T')[0]!,
        end,
        granularity: '1day',
      }
    case TimeRange.SIX_MONTHS:
      return {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        start: dateToISOString(addDaysToDate(now, -180)).split('T')[0]!,
        end,
        granularity: '1day',
      }
    case TimeRange.ONE_YEAR:
      return {
        // biome-ignore lint/style/noNonNullAssertion: suppress noUncheckedIndexedAccess
        start: dateToISOString(addDaysToDate(now, -365)).split('T')[0]!,
        end,
        granularity: '1day',
      }
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ALL:
    default:
      return { start: '2020-01-01', end, granularity: '1day' }
  }
}

/**
 * Formats dates for X-axis labels based on the selected time range.
 *
 * @param date - Date to format
 * @param range - Selected time range
 * @returns Formatted date string
 *
 * @example
 * // For '1D' (One Day):
 * // Returns time in 24h format (e.g., "14:30")
 * formatXAxisDate(new Date(), '1D')
 *
 * @example
 * // For '1W', '1M', '3M':
 * // Returns date with month and day (e.g., "Feb 20")
 * formatXAxisDate(new Date(), '1W')
 */
export function formatXAxisDate(date: Date, range: string): string {
  switch (range) {
    case TimeRange.ONE_DAY:
      return formatDate(date, DateFormat.TIME_24)
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_WEEK:
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_MONTH:
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.THREE_MONTHS:
    // biome-ignore lint/suspicious/useDefaultSwitchClauseLast: Bulk suppression as part of biome migration
    default:
      return formatDate(date, 'MMM d')
    case TimeRange.SIX_MONTHS:
    case TimeRange.ONE_YEAR:
    case TimeRange.ALL:
      return formatDate(date, `MMM ''yy`)
  }
}

/**
 * Formats dates for tooltip display based on the selected time range.
 * Provides more detailed date information than X-axis labels.
 *
 * @param date - Date to format
 * @param range - Selected time range
 * @returns Formatted date string
 *
 * @example
 * // For '1M' (One Month):
 * // Returns time and date (e.g., "Fri, Feb 20")
 * formatTooltipDate(new Date(), '1M')
 */
export function formatTooltipDate(date: Date, range: string): string {
  switch (range) {
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_DAY:
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_WEEK:
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_MONTH:
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.THREE_MONTHS:
    // biome-ignore lint/suspicious/useDefaultSwitchClauseLast: Bulk suppression as part of biome migration
    default:
      return formatDate(date, DateFormat.SHORT_WEEKDAY_DATE)
    case TimeRange.SIX_MONTHS:
    case TimeRange.ONE_YEAR:
    case TimeRange.ALL:
      return formatDate(date, DateFormat.MEDIUM)
  }
}

export function formatTooltipTime(date: Date, range: string): string {
  switch (range) {
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_DAY:
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_WEEK:
    // biome-ignore lint/suspicious/useDefaultSwitchClauseLast: Bulk suppression as part of biome migration
    default:
      return formatDate(date, DateFormat.TIME_24)
    case TimeRange.ONE_MONTH:
    case TimeRange.THREE_MONTHS:
    case TimeRange.SIX_MONTHS:
    case TimeRange.ONE_YEAR:
    case TimeRange.ALL:
      return ''
  }
}

// Constants for data point intervals
const INTERVALS = {
  // Shows data points every ~45 minutes for 1 day (on prod we poll every 3 minutes)
  ONE_DAY_POINTS: 14,
  // Shows data points every business day (in 15min intervals) for 1 week
  ONE_WEEK_POINTS: 25,
  // Shows data points every week (skipping weekends) for 1 month
  ONE_MONTH_POINTS: 4,
  // Shows data points every 1.5 weeks for 3 months
  THREE_MONTH_POINTS: 7,
  // Shows data points every month for 6M, 1Y, and All time ranges
  EXTENDED_RANGE_POINTS: 21,
} as const

/**
 * Returns the number of data points to display on the X-axis based on the time range.
 * Helps maintain readable charts by adjusting data density for different time periods.
 *
 * @param range - Selected time range
 * @returns Number of intervals to display
 */
export const getIntervalByTimeRange = (range: string): number => {
  switch (range) {
    case TimeRange.ONE_DAY:
      return INTERVALS.ONE_DAY_POINTS
    case TimeRange.ONE_WEEK:
      return INTERVALS.ONE_WEEK_POINTS
    // biome-ignore lint/complexity/noUselessSwitchCase: Bulk suppression as part of biome migration
    case TimeRange.ONE_MONTH:
    // biome-ignore lint/suspicious/useDefaultSwitchClauseLast: Bulk suppression as part of biome migration
    default:
      return INTERVALS.ONE_MONTH_POINTS
    case TimeRange.THREE_MONTHS:
      return INTERVALS.THREE_MONTH_POINTS
    case TimeRange.SIX_MONTHS:
    case TimeRange.ONE_YEAR:
    case TimeRange.ALL:
      return INTERVALS.EXTENDED_RANGE_POINTS
  }
}

export const timeRangeOptions = [
  { label: '1D', value: '1D' },
  { label: '1W', value: '1W' },
  { label: '1M', value: '1M' },
  { label: '3M', value: '3M' },
  { label: '6M', value: '6M' },
  { label: '1Y', value: '1Y' },
  { label: 'All', value: 'All' },
]

export const displayModeOptions = [
  { label: 'Yield', value: DisplayMode.YIELD },
  { label: 'Price', value: DisplayMode.PRICE },
]
